// ---------------------------------------------------------------------------
// The campaign funnel — where each lead stands, in words a client reads
//
// The campaign page's top row used to be operator counters side by side:
// "Pending 445" (status pending) next to "Waiting 450" (next step parked, e.g.
// outside sending hours — at night nearly every lead). Both true, overlapping,
// and together almost twice the campaign. A client could not tell which
// number was the campaign. (Ferenc, 28 October 2026; T5 / WP51.)
//
// The row is now a funnel: every lead in EXACTLY one stage, so the cards add
// up to "Leads". The operator counters are still there, behind "Show details".
//
// The stage is decided by precedence, first match wins: replied, stopped, then
// the furthest step of ours that went out. The predicates are the ones the
// status chip and the filter menu use (lead-state-chip.ts), not new readings
// of the same fields, so a funnel card and the filter it opens cannot disagree.
//
// REVIEW, 28 October: "the sequence is over" (`doneAt`) is NOT a stage. The
// last step's send stamps it at once (linkedin-tick-worker.ts, DM ~1622, email
// ~2118), so ranking it ahead of the sends emptied Messaged. A finished lead
// stays in the furthest stage it reached; the progress line counts it.
// ---------------------------------------------------------------------------

import { chipPredicates, type LeadStateChipInput } from './lead-state-chip.js';

export type FunnelStage = 'notContacted' | 'invited' | 'connected' | 'messaged' | 'replied' | 'stopped';

/** The stages in the order a lead moves through them — the order of the cards. */
export const FUNNEL_STAGES: readonly FunnelStage[] = [
  'notContacted',
  'invited',
  'connected',
  'messaged',
  'replied',
  'stopped',
];

/** Why outreach stopped before a reply, in the same precedence as `funnelStage`. */
export type StoppedReason = 'optedOut' | 'bounced' | 'inviteIgnored' | 'finishedUncontacted';

/** The lead fields the funnel reads. A subset of the page's LeadData. */
export interface LeadFunnelInput extends LeadStateChipInput {
  /** Whether an email step could still reach them after an ignored invitation. */
  email?: string;
  /**
   * NOT read as "a message went out". The server stamps it on the first
   * outreach of ANY kind — a LinkedIn connection request included
   * (linkedin-tick-worker.ts, campaign-sync.ts) — so on a LinkedIn campaign
   * every invited lead carries it. Listed so the type says it was considered.
   */
  sentAt?: string;
  /**
   * NOT read either. The SENT-folder echo writes the message-id onto every lead
   * at that address, in every campaign (recordSentEmailMessageId), so it is no
   * proof that THIS campaign emailed them.
   */
  sentEmailMessageIds?: string[];
  /** Set by the first email this campaign sent (Unipile's provider id, kept for threading). */
  emailThreadProviderId?: string;
  /** The mailbox the old email provider (Instantly) sent from. */
  sentFromEmailAccount?: string;
}

/** What the page knows and the lead does not. */
export interface FunnelContext {
  /** The page's `isLeadCompleted`: status completed, or a reply of ours went out. */
  isCompleted: boolean;
  /** The campaign's sequence has an email step — it can still reach a lead LinkedIn has given up on. */
  campaignHasEmailStep: boolean;
  /** An email step of this lead completed (from the campaign's job list). */
  emailSent?: boolean;
}

/**
 * The sequence is over for this lead: `doneAt`, completed per the page, or
 * stopped (stoppedBecause) — opted out, bounced, or an ignored invitation
 * nothing else can follow. A Do Not Contact lead is derived by the list route
 * at read time and gets `doneAt` only when its next job reaches the send gate —
 * with daily caps that can take weeks, and it is finished all the same.
 */
export function isSequenceFinished(lead: LeadFunnelInput, ctx: FunnelContext): boolean {
  return !!lead.doneAt || ctx.isCompleted || stoppedBecause(lead, ctx) !== null;
}

/**
 * Whether an email of this campaign went out to the lead. Our own record first
 * (a completed email job, from the page), then the traces only this lead's own
 * send leaves. Never `sentAt` or `sentEmailMessageIds`, see LeadFunnelInput.
 */
function emailWentOut(lead: LeadFunnelInput, ctx: FunnelContext): boolean {
  return !!ctx.emailSent || !!lead.emailThreadProviderId || !!lead.sentFromEmailAccount;
}

/**
 * Outreach stopped for a reason of the lead's own, or `null`. An ignored
 * invitation ends only the LinkedIn steps (WP47; evaluate-lead-done.ts keeps an
 * email step alive), so it stops the lead only if no email of ours went out
 * AND the server closed the lead or no email step can reach them. Once an email
 * went out the lead is Messaged — also after that email, often the last step,
 * stamps `doneAt`.
 */
function stoppedBecause(lead: LeadFunnelInput, ctx: FunnelContext): StoppedReason | null {
  if (chipPredicates.optedOut(lead)) return 'optedOut';
  if (chipPredicates.bounced(lead)) return 'bounced';
  if (
    chipPredicates.inviteSentIgnored(lead) &&
    !emailWentOut(lead, ctx) &&
    (!!lead.doneAt || ctx.isCompleted || !ctx.campaignHasEmailStep || !lead.email)
  ) {
    return 'inviteIgnored';
  }
  return null;
}

/**
 * The one stage a lead is in. First match wins:
 *
 *   replied   — exactly chipPredicates.replied, so the Replied card keeps the
 *               number the Replied filter lists. Ahead of an opt-out on
 *               purpose: here the question is what the outreach achieved,
 *               not whether we may contact them again (the chip's question).
 *   stopped   — opted out, bounced, or an ignored invitation nothing else
 *               can follow (stoppedBecause).
 *   messaged  — a DM or an email of this campaign went out.
 *   connected — they accepted our invitation. Already being a connection is
 *               not a step of ours; such a lead is where its sends put it.
 *   invited   — our invitation went out (chipPredicates.connSent).
 *   stopped   — again, for a lead whose sequence finished with none of the
 *               above: "finished without being contacted".
 *   notContacted — nothing of ours has gone out yet, including a lead whose
 *               next step is parked outside sending hours.
 */
export function funnelStage(lead: LeadFunnelInput, ctx: FunnelContext): FunnelStage {
  if (chipPredicates.replied(lead)) return 'replied';
  if (stoppedBecause(lead, ctx)) return 'stopped';
  if (!!lead.dmSentAt || emailWentOut(lead, ctx)) return 'messaged';
  if (lead.acceptedAt) return 'connected';
  if (chipPredicates.connSent(lead)) return 'invited';
  if (isSequenceFinished(lead, ctx)) return 'stopped';
  return 'notContacted';
}

/**
 * Why a stopped lead stopped — `null` for a lead in any other stage (a reply
 * outranks an opt-out), so the reasons always add up to the Stopped card.
 */
export function stoppedReason(lead: LeadFunnelInput, ctx: FunnelContext): StoppedReason | null {
  if (funnelStage(lead, ctx) !== 'stopped') return null;
  return stoppedBecause(lead, ctx) ?? 'finishedUncontacted';
}

/**
 * Still in play: the lead neither replied, stopped nor finished. A lead that
 * did needs nothing more from anyone — the server's own check for a stuck lead
 * excludes it too — so No Invite, State Unknown and "need attention" count
 * only leads still in play.
 */
export function isStillInPlay(lead: LeadFunnelInput, ctx: FunnelContext): boolean {
  const stage = funnelStage(lead, ctx);
  return stage !== 'replied' && stage !== 'stopped' && !isSequenceFinished(lead, ctx);
}

/**
 * A lead an operator has to look at: it cannot move on its own (no invitation
 * outstanding, none queued) or its LinkedIn state is unreadable — and it is
 * still in play.
 */
export function needsAttention(lead: LeadFunnelInput, ctx: FunnelContext): boolean {
  return (chipPredicates.noInvite(lead) || chipPredicates.connectionUnknown(lead)) && isStillInPlay(lead, ctx);
}

/**
 * A share as percent text that never hides a small number. `Math.round` made
 * 2 of 452 read "0%"; below 10 % one decimal ("0.4%"), below 0.1 % "<0.1%",
 * and "100%" only when every one is counted (999 of 1000 is "99%").
 */
export function formatShare(part: number, total: number): string {
  if (!(total > 0) || !(part > 0)) return '0%';
  if (part >= total) return '100%';
  const pct = (part / total) * 100;
  if (pct < 0.1) return '<0.1%';
  const oneDecimal = Math.round(pct * 10) / 10;
  // 9.96 % rounds to "10.0" — from there it is a whole number like the rest.
  if (oneDecimal < 10) return `${oneDecimal.toFixed(1)}%`;
  return `${Math.min(Math.round(pct), 99)}%`;
}
